/**
 * src/features/billing/ui/TrialBanner.tsx
 *
 * Trial countdown pill rendered in the page header when access==='trial'.
 * Reads state from useAuthStore; on click goes directly to checkout via
 * useUpgradeModal.openUpgrade('sync') — per CONTEXT.md decision D-04.
 *
 * T-16-09: Banner is display-only; forged trialEndsAt only affects the countdown
 * label. Server access decision is independent (Phase 15 requireAccess).
 * T-16-10: Trial day-count is non-sensitive UX; store is session-ephemeral.
 *
 * Visual spec: UI-SPEC.md Surface 3 — Trial Countdown Banner.
 * Analog: PlanBadge.tsx (null-return guard + header pill pattern).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useUpgradeModal } from '@/features/billing/lib/useUpgradeModal';

/**
 * Days threshold below which the banner escalates to amber (urgent) styling.
 * Rationale: 3 days gives users meaningful lead-time before trial expires.
 * Matches UI-SPEC.md "Urgent state (daysLeft <= 3)".
 */
const URGENT_DAYS_THRESHOLD = 3;

/**
 * Compute days remaining in trial from a Unix-ms timestamp.
 * Extracted as a pure helper outside the component — Date.now() here (not in
 * the component body) satisfies react-hooks/purity (same pattern as
 * SyncStatusBadge.tsx elapsedMinutes).
 *
 * trialEndsAt is Unix milliseconds per Phase 15 API contract.
 * Math.ceil so "1 day" shows until the last moment of the final day.
 * Math.max(0, ...) prevents negative values during edge sync windows.
 */
function computeDaysLeft(trialEndsAt: number): number {
  return Math.max(
    0,
    Math.ceil((trialEndsAt - Date.now()) / (1000 * 60 * 60 * 24)),
  );
}

export function TrialBanner(): React.ReactElement | null {
  const { t } = useTranslation();
  const { access, trialEndsAt } = useAuthStore();
  const { openUpgrade } = useUpgradeModal();

  // Guard: only render during active trial with a valid end timestamp
  if (access !== 'trial' || trialEndsAt === null) {
    return null;
  }

  const daysLeft = computeDaysLeft(trialEndsAt);
  const isUrgent = daysLeft <= URGENT_DAYS_THRESHOLD;
  const isLastDay = daysLeft === 1;
  const isExpired = daysLeft === 0;

  // Build class string based on urgency state
  const pillClasses = [
    'flex items-center gap-1.5 px-3 py-1.5 rounded-full',
    'text-micro font-bold uppercase tracking-wider',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
    // Color: blue (non-urgent) or amber (urgent ≤3 days)
    isUrgent
      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 ring-1 ring-amber-300 dark:ring-amber-700 focus-visible:ring-amber-500'
      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 focus-visible:ring-blue-500',
    // Pulse only on the last day (Tailwind animate-pulse — NOT framer-motion).
    // motion-reduce:animate-none respects prefers-reduced-motion (no global kill-switch exists).
    isLastDay ? 'animate-pulse motion-reduce:animate-none' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Label branches: n=0 → "Last day", n=1 → singular (avoids "1 days left" in
  // en/pl), n≥2 → "{{n}} days left". Mirrors the existing isExpired special-case.
  // aria-label stays in lockstep with the visible label for screen-reader parity (WR-03).
  let label: string;
  let ariaLabel: string;
  if (isExpired) {
    label = t('billing.trial_last_chance', 'Last day');
    ariaLabel = t('billing.trial_last_chance_aria', 'Last day of trial. Click to proceed to payment.');
  } else if (isLastDay) {
    label = t('billing.trial_one_day', '1 day left');
    ariaLabel = t('billing.trial_one_day_aria', '1 day of trial remaining. Click to proceed to payment.');
  } else {
    label = t('billing.trial_left', '{{n}} days left', { n: daysLeft });
    ariaLabel = t(
      'billing.trial_left_aria',
      '{{n}} days of trial remaining. Click to proceed to payment.',
      { n: daysLeft },
    );
  }

  return (
    <button
      className={pillClasses}
      onClick={() => openUpgrade('sync')}
      aria-label={ariaLabel}
    >
      <Clock size={12} aria-hidden="true" />
      {/* Compact day count on mobile so the pill signals urgency without the full
          label; omitted when expired (icon + urgency color carry it). aria-hidden
          because the button's aria-label already conveys the full state. */}
      {!isExpired && (
        <span className="sm:hidden" aria-hidden="true">
          {daysLeft}
        </span>
      )}
      {/* Full label on sm+ */}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
